/**
 * tenantApi.js — Frontend adapter for the multi-tenant management API.
 *
 * Backend contract (same-origin, under `/api/tenants`).
 * Requests are stamped with:
 *   · `X-User-Id`         — caller identity (written by auth middleware)
 *   · `X-Tenant-Id`       — currently-active tenant, supplied by the
 *                           TenantContext provider on every call to a
 *                           tenant-scoped endpoint
 *
 * The helpers never include a tenant header on the caller-self routes
 * (`/me/memberships`) — those must resolve for users with zero active
 * memberships too.
 */
import { get, post, put, patch, del, requireParam } from './httpClient';

const BASE = '/api/tenants';

export const listMyMemberships = () => get(`${BASE}/me/memberships`);

export const listTenants = () => get(BASE);

export const createTenant = (payload) => {
  requireParam('name', payload?.name);
  requireParam('slug', payload?.slug);
  return post(BASE, payload);
};

export const getTenant = (tenantId) => {
  requireParam('tenantId', tenantId);
  return get(`${BASE}/${tenantId}`);
};

export const updateTenant = (tenantId, patchBody) => {
  requireParam('tenantId', tenantId);
  return put(`${BASE}/${tenantId}`, patchBody);
};

export const deleteTenant = (tenantId) => {
  requireParam('tenantId', tenantId);
  return del(`${BASE}/${tenantId}`);
};

export const listMembers = (tenantId) => {
  requireParam('tenantId', tenantId);
  return get(`${BASE}/${tenantId}/members`);
};

export const inviteMember = (tenantId, body) => {
  requireParam('tenantId', tenantId);
  requireParam('email', body?.email);
  return post(`${BASE}/${tenantId}/members`, body);
};

export const updateMember = (tenantId, userId, body) => {
  requireParam('tenantId', tenantId);
  requireParam('userId', userId);
  return patch(`${BASE}/${tenantId}/members/${userId}`, body);
};

export const removeMember = (tenantId, userId) => {
  requireParam('tenantId', tenantId);
  requireParam('userId', userId);
  return del(`${BASE}/${tenantId}/members/${userId}`);
};

export const listAudit = (tenantId, limit = 100) => {
  requireParam('tenantId', tenantId);
  return get(`${BASE}/${tenantId}/audit`, { limit });
};
